import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AuthContext } from '../App';

export default function ReportPage() {
  const { user, setUser } = useContext(AuthContext);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const token = localStorage.getItem('vi_token');
  const api = axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    loadReport();
    // Start 24h countdown
    const saved = localStorage.getItem('vi_countdown_start');
    if (saved) {
      const start = parseInt(saved);
      const elapsed = Date.now() - start;
      const remaining = 24 * 60 * 60 * 1000 - elapsed;
      if (remaining > 0) setCountdown(remaining);
    } else {
      localStorage.setItem('vi_countdown_start', Date.now().toString());
      setCountdown(24 * 60 * 60 * 1000);
    }
  }, []);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1000) { clearInterval(timer); return 0; }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const formatTime = (ms) => {
    if (!ms || ms <= 0) return '00:00:00';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const loadReport = async () => {
    try {
      const res = await api.get('/report/status');
      setReportData(res.data);
    } catch {}
    setLoading(false);
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      await api.post('/report/generate');
      toast.success('Report generato con successo!');
      loadReport();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Errore generazione report');
    }
    setGenerating(false);
  };

  const handleCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post('/api/create-checkout-session', {
        amount: 49.90,
        type: 'report',
        email: user?.email || undefined
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Errore checkout:', err);
      toast.error('Errore durante il pagamento. Riprova tra qualche istante.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) { toast.error('Inserisci un codice'); return; }
    setApplyingPromo(true);
    try {
      await api.post('/report/promo', { codice: promoCode });
      toast.success('Codice applicato! Report sbloccato!');
      setUser(prev => ({ ...prev, report_sbloccato: 1 }));
      loadReport();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Codice non valido');
    }
    setApplyingPromo(false);
  };

  const downloadReport = async () => {
    try {
      const res = await api.get('/report/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'report_incentivi.pdf';
      a.click();
    } catch {
      toast.error('Errore download');
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner"></div></div>;

  const sbloccato = reportData?.sbloccato;

  return (
    <div className="fade-in" style={{ padding: '30px 0' }}>
      <div className="container" style={{ maxWidth: 900 }}>
        <h1 style={{ marginBottom: 8 }}>Report Incentivi Personalizzato</h1>
        <p style={{ color: '#6b7280', marginBottom: 24 }}>
          {reportData?.incentivi_count || 0} incentivi trovati compatibili con il tuo profilo
        </p>

        {/* Countdown */}
        {!sbloccato && countdown > 0 && (
          <div className="countdown">
            <div style={{ fontSize: 14, marginBottom: 4 }}>Offerta speciale - Il prezzo aumentera tra:</div>
            <div className="time">{formatTime(countdown)}</div>
            <div className="label">Sblocca ora a soli €49,90 invece di €197</div>
          </div>
        )}

        {/* Generate Report Button */}
        {!reportData?.has_report && (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#128202;</div>
            <h3 style={{ marginBottom: 8 }}>Genera il Tuo Report</h3>
            <p style={{ color: '#6b7280', marginBottom: 20 }}>
              Clicca per generare il report personalizzato con tutti gli incentivi compatibili con la tua azienda.
            </p>
            <button onClick={generateReport} className="btn btn-lg btn-green" disabled={generating}>
              {generating ? 'Generazione in corso...' : 'Genera Report'}
            </button>
          </div>
        )}

        {/* Report Preview */}
        {reportData?.has_report && (
          <>
            {/* Preview with blur */}
            <div className="card" style={{ marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
              <h3 style={{ marginBottom: 16 }}>Anteprima Report</h3>

              {/* First 3 incentivi visible */}
              {reportData.incentivi_preview?.map((inc, i) => (
                <div key={i} style={{ padding: 16, borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <strong>{inc.nome}</strong>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{inc.tipo} | {inc.livello}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: '#16a34a' }}>{inc.compatibilita}%</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{inc.importo_max}</div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Blurred section */}
              {!sbloccato && (
                <div style={{ position: 'relative' }}>
                  <div className="report-blur" style={{ padding: 16 }}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{ padding: 16, borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <strong>Incentivo nascosto #{i + 3}</strong>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>Tipo agevolazione | Livello</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700 }}>XX%</div>
                            <div style={{ fontSize: 12 }}>€XXX.XXX</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="report-overlay">
                    <div style={{ fontSize: 48, marginBottom: 12 }}>&#128274;</div>
                    <h3 style={{ marginBottom: 8 }}>Report Bloccato</h3>
                    <p style={{ color: '#6b7280', marginBottom: 16, maxWidth: 400, textAlign: 'center' }}>
                      Sblocca il report completo per vedere tutti i {reportData.incentivi_count} incentivi compatibili con la tua azienda
                    </p>
                    <button onClick={handleCheckout} disabled={checkoutLoading} className="btn btn-lg btn-green" style={{ border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer' }}>
                      {checkoutLoading ? 'Caricamento...' : 'Sblocca Report - €49,90'}
                    </button>
                    <p style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>oppure inserisci un codice promozionale</p>
                  </div>
                </div>
              )}
            </div>

            {/* Unlock section */}
            {!sbloccato && (
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="grid-2" style={{ alignItems: 'center' }}>
                  <div>
                    <h3 style={{ marginBottom: 8 }}>Sblocca il Report Completo</h3>
                    <p style={{ color: '#6b7280', fontSize: 14 }}>
                      Accedi a tutti i {reportData.incentivi_count} incentivi, con dettagli completi, requisiti, scadenze e istruzioni per la presentazione delle domande.
                    </p>
                    <button onClick={handleCheckout} disabled={checkoutLoading} className="btn btn-green" style={{ marginTop: 12, border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer' }}>
                      {checkoutLoading ? 'Caricamento...' : 'Paga €49,90 con Stripe'}
                    </button>
                  </div>
                  <div>
                    <h4 style={{ marginBottom: 8 }}>Hai un codice promozionale?</h4>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={promoCode}
                        onChange={e => setPromoCode(e.target.value)}
                        placeholder="Inserisci codice"
                        style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8 }}
                      />
                      <button onClick={applyPromo} className="btn btn-primary" disabled={applyingPromo}>
                        {applyingPromo ? '...' : 'Applica'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Download section (unlocked) */}
            {sbloccato && (
              <div className="card" style={{ textAlign: 'center', padding: 32, background: '#f0fdf4' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>&#127881;</div>
                <h3 style={{ color: '#16a34a', marginBottom: 8 }}>Report Sbloccato!</h3>
                <p style={{ color: '#6b7280', marginBottom: 16 }}>
                  Il tuo report con {reportData.incentivi_count} incentivi e pronto per il download.
                </p>
                <button onClick={downloadReport} className="btn btn-lg btn-green">
                  Scarica Report PDF
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
